import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LuppApi } from '../lupp.service';
import { LUPP_QUERY_KEYS } from '../constants.lupp';
import type { TransformedCheckoutDetail } from '../inventory/transformers.checkout';
import {
  QuotesListResponse,
  GetQuotesListRequest,
  UseGetQuotesParams,
  UseGetQuoteByIdParams,
  UpdateQuoteRequest,
  UseUpdateQuoteParams,
  CreateQuoteRequest,
  UseCreateQuoteParams,
  SendDocusignRequest,
  SendDocusignResponse,
  UseSendDocusignParams,
} from './dto.quotes';
import { transformQuoteToCheckoutDetail, type QuoteDetailsResponse } from './transformers.quotes';

/*
|--------------------------------------------------------------------------
| GET QUOTES
|--------------------------------------------------------------------------
*/

/**
 * Hook to fetch paginated quotes list for a prospect
 * @param params - Prospect ID and query parameters for quotes list
 * @returns Query result with quotes data
 * @example
 * const { data, isLoading, error } = useGetQuotes({
 *   prospectId: 'd3a1f7b2-8e4c-44aa-9c77-1b2c3d4e5f66',
 *   page: 0,
 *   size: 25
 * });
 */

export const useGetQuotes = ({
  page = 0,
  size = 10,
  searchString,
  sortField,
  sortOrder = 'desc',
  enabled = true,
  prospectId,
}: UseGetQuotesParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.QUOTES, prospectId, page, size, searchString, sortField, sortOrder],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: String(page),
        size: String(size),
      });

      const body: GetQuotesListRequest = {};
      if (searchString) body.searchString = searchString;
      if (sortField) body.sortField = sortField;
      if (sortOrder) body.sortOrder = sortOrder;

      const response = await LuppApi.post<GetQuotesListRequest, QuotesListResponse>(
        `sales/v1/orders/prospects/${prospectId}/quotes/searches?${queryParams.toString()}`,
        body,
        {
          extractData: (response) => response,
        }
      );
      return {
        items: response.data,
        totalCount: response.totalCount,
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        currentPageCount: response.currentPageCount,
        totalPages: response.totalPages,
      };
    },
    placeholderData: (previousData) => previousData,
    enabled: enabled && !!prospectId,
  });
};

/*
|--------------------------------------------------------------------------
| GET QUOTE BY ID
|--------------------------------------------------------------------------
*/

/**
 * Hook to fetch a single quote by ID
 * @param params - Quote ID and enabled flag
 * @returns Query result with transformed checkout detail data
 * @example
 * const { data, isLoading, error } = useGetQuoteById({
 *   prospectId: 'd3a1f7b2-8e4c-44aa-9c77-1b2c3d4e5f66',
 *   quoteId: 'L2135674613'
 * });
 */

export const useGetQuoteById = ({ prospectId, quoteId, enabled = true }: UseGetQuoteByIdParams) => {
  return useQuery<TransformedCheckoutDetail>({
    queryKey: [LUPP_QUERY_KEYS.QUOTES, quoteId],
    queryFn: async () => {
      // TODO: Uncomment when API is ready
      const response = await LuppApi.get<QuoteDetailsResponse>(
        `sales/v1/orders/prospects/${prospectId}/quotes/${quoteId}`
      );
      return transformQuoteToCheckoutDetail(response);
    },
    enabled: enabled && !!quoteId && !!prospectId,
  });
};

/*
|--------------------------------------------------------------------------
| UPDATE QUOTE
|--------------------------------------------------------------------------
*/

/**
 * Hook to update an existing quote
 * @param params - Prospect ID and Quote ID
 * @returns Mutation result for updating a quote
 * @example
 * const { mutate, isPending } = useUpdateQuote({ prospectId: 'P123', quoteId: 'Q456' });
 * mutate({ preferredPaymentMethod: 'Cash', ... });
 */
export const useUpdateQuote = ({ prospectId, quoteId }: UseUpdateQuoteParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateQuoteRequest) => {
      const response = await LuppApi.put<UpdateQuoteRequest, QuoteDetailsResponse>(
        `sales/v1/orders/prospects/${prospectId}/quotes/${quoteId}`,
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.QUOTES] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.QUOTES, quoteId] });
    },
    onError: (error) => {
      console.error('Failed to update quote:', error);
    },
  });
};

/*
|--------------------------------------------------------------------------
| CREATE QUOTE
|--------------------------------------------------------------------------
*/

/**
 * Hook to create a new quote for a prospect
 * @param params - Prospect ID
 * @returns Mutation result for creating a quote
 * @example
 * const { mutate, isPending } = useCreateQuote({ prospectId: 'P123' });
 * mutate({ model: 'Air', vin: '...', preferredPaymentMethod: 'Cash' });
 */
export const useCreateQuote = ({ prospectId }: UseCreateQuoteParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateQuoteRequest) => {
      const response = await LuppApi.post<CreateQuoteRequest, QuoteDetailsResponse>(
        `sales/v1/orders/prospects/${prospectId}/quotes`,
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.QUOTES] });
    },
    onError: (error) => {
      console.error('Failed to create quote:', error);
    },
  });
};

/*
|--------------------------------------------------------------------------
| DOWNLOAD QUOTE
|--------------------------------------------------------------------------
*/

/**
 * Parameters for useDownloadQuote hook
 */
export type UseDownloadQuoteParams = {
  prospectId: string;
  quoteId: string;
  enabled?: boolean;
};

/**
 * Hook to download a quote PDF
 * @param params - Prospect ID and Quote ID
 * @returns Query result with download data
 * @example
 * const { data, isLoading } = useDownloadQuote({ prospectId: 'P123', quoteId: 'Q456' });
 */
export const useDownloadQuote = ({
  prospectId,
  quoteId,
  enabled = true,
}: UseDownloadQuoteParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.QUOTES, prospectId, quoteId, 'download'],
    queryFn: async () => {
      const response = await LuppApi.instance.get<Blob>(
        `sales/v1/orders/prospects/${prospectId}/quotes/${quoteId}/download`,
        { responseType: 'blob' }
      );

      // Extract filename from Content-Disposition header
      // Note: Backend must expose this header via CORS for it to be accessible
      const contentDisposition = response.headers['content-disposition'] as string | undefined;
      const filename =
        contentDisposition?.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i)?.[1]?.trim() ||
        `quote-${quoteId}.pdf`;

      return { blob: response.data, filename: decodeURIComponent(filename) };
    },
    enabled: Boolean(prospectId && quoteId && enabled),
  });
};

/*
|--------------------------------------------------------------------------
| SEND DOCUSIGN
|--------------------------------------------------------------------------
*/

/**
 * Hook to send Docusign for a quote
 * @param params - Prospect ID and Quote ID
 * @returns Mutation result for sending Docusign
 * @example
 * const { mutate, isPending } = useSendDocusign({ prospectId: 'P123', quoteId: 'Q456' });
 * mutate({ firstName: 'John', lastName: 'Doe', email: 'john@example.com' });
 */
export const useSendDocusign = ({ prospectId, quoteId }: UseSendDocusignParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SendDocusignRequest) => {
      const response = await LuppApi.post<SendDocusignRequest, SendDocusignResponse>(
        `sales/v1/orders/prospects/${prospectId}/quotes/${quoteId}/send-docusign`,
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.QUOTES] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.QUOTES, quoteId] });
    },
  });
};
